import { Link } from 'react-router-dom';
import { 
  BarChart3, 
  Users, 
  Mail, 
  DollarSign, 
  Swords, 
  Activity, 
  Newspaper, 
  Crown, 
  GraduationCap, 
  Banknote, 
  BadgeCheck,
  Wrench,
  FileText,
  Plus,
  Shield,
  Share2,
  Calculator,
  ScrollText,
  UserCheck,
  MailOpen,
  Megaphone,
  Package,
  ShoppingBag,
  Briefcase,
  CalendarDays,
  Layout,
  MessageSquare,
  BookOpen
} from 'lucide-react';
import { Badge } from '@/components/ui/badge';
import {
  Sidebar,
  SidebarContent,
  SidebarGroup,
  SidebarGroupContent,
  SidebarGroupLabel,
  SidebarMenu,
  SidebarMenuButton,
  SidebarMenuItem,
  SidebarHeader,
  useSidebar,
} from '@/components/ui/sidebar';

interface AdminSidebarProps {
  activeTab: string;
  onTabChange: (tab: string) => void;
  pendingSubmissionsCount: number;
}

const menuItems = [
  { id: 'analytics', label: 'Analytics', icon: BarChart3, group: 'overview' },
  { id: 'tools', label: 'Manage Tools', icon: Wrench, group: 'content' },
  { id: 'skills', label: 'Skills', icon: BookOpen, group: 'content' },
  { id: 'submissions', label: 'Submissions', icon: FileText, group: 'content', showBadge: true },
  { id: 'add', label: 'Add New Tool', icon: Plus, group: 'content' },
  { id: 'blog', label: 'Blog', icon: BookOpen, group: 'content' },
  { id: 'users', label: 'Users', icon: Users, group: 'management' },
  { id: 'creators', label: 'AI Creators', icon: UserCheck, group: 'management' },
  { id: 'newsletter', label: 'Newsletter', icon: Mail, group: 'management' },
  { id: 'playbook-leads', label: 'Playbook Leads', icon: Mail, group: 'management' },
  { id: 'contact', label: 'Contact Messages', icon: MessageSquare, group: 'management' },
  { id: 'affiliates', label: 'Affiliates', icon: DollarSign, group: 'monetization' },
  { id: 'ads', label: 'Ad Placements', icon: Megaphone, group: 'monetization' },
  { id: 'bundles', label: 'Tool Bundles', icon: Package, group: 'monetization' },
  { id: 'digital-products', label: 'Digital Products', icon: ShoppingBag, group: 'monetization' },
  { id: 'accounting', label: 'Accounting', icon: Calculator, group: 'monetization' },
  { id: 'battles', label: 'Battles', icon: Swords, group: 'engagement' },
  { id: 'health', label: 'Health', icon: Activity, group: 'engagement' },
  { id: 'news', label: 'News', icon: Newspaper, group: 'content' },
  { id: 'jobs', label: 'Jobs', icon: Briefcase, group: 'content' },
  { id: 'events', label: 'Events', icon: CalendarDays, group: 'content' },
  { id: 'wall', label: 'Wall of Fame', icon: Crown, group: 'engagement' },
  { id: 'learning', label: 'Learning', icon: GraduationCap, group: 'content' },
  { id: 'blueprints', label: 'Blueprints', icon: Banknote, group: 'monetization' },
  { id: 'verification', label: 'Verification', icon: BadgeCheck, group: 'management' },
  { id: 'ownership', label: 'Ownership Claims', icon: Shield, group: 'management' },
  { id: 'agreements', label: 'T&C Agreements', icon: ScrollText, group: 'management' },
  { id: 'social', label: 'Social Links', icon: Share2, group: 'settings' },
  { id: 'emails', label: 'Email Templates', icon: MailOpen, group: 'settings' },
  { id: 'homepage', label: 'Homepage Widgets', icon: Layout, group: 'settings' },
  { id: 'ebook', label: 'eBook Generator', icon: BookOpen, group: 'monetization', isLink: true, href: '/admin/generate-ebook' },
];

// Valid ?tab= ids (link-only items excluded; 'add' is reachable from the menu).
export const ADMIN_TAB_IDS: ReadonlySet<string> = new Set(
  menuItems.filter(item => !('isLink' in item && item.isLink)).map(item => item.id),
);

const groups = [
  { id: 'overview', label: 'Overview' },
  { id: 'content', label: 'Content' },
  { id: 'management', label: 'Management' },
  { id: 'monetization', label: 'Monetization' },
  { id: 'engagement', label: 'Engagement' },
  { id: 'settings', label: 'Settings' },
];

export function AdminSidebar({ activeTab, onTabChange, pendingSubmissionsCount }: AdminSidebarProps) {
  const { state, isMobile, setOpenMobile } = useSidebar();
  const isCollapsed = state === 'collapsed';

  return (
    <Sidebar collapsible="icon" className="border-r">
      <SidebarHeader className="border-b px-4 py-3">
        <Link to="/" className="flex items-center gap-2 hover:opacity-80 transition-opacity">
          <img 
            src="/favicon.png" 
            alt="Home" 
            className="h-8 w-8 shrink-0"
          />
          {!isCollapsed && (
            <span className="font-display font-bold text-lg">Admin Panel</span>
          )}
        </Link>
      </SidebarHeader>

      <SidebarContent>
        {groups.map((group) => {
          const groupItems = menuItems.filter(item => item.group === group.id);
          if (groupItems.length === 0) return null;

          return (
            <SidebarGroup key={group.id}>
              <SidebarGroupLabel>{group.label}</SidebarGroupLabel>
              <SidebarGroupContent>
                <SidebarMenu>
                  {groupItems.map((item) => (
                    <SidebarMenuItem key={item.id}>
                      {'isLink' in item && (item as any).isLink ? (
                        <SidebarMenuButton asChild tooltip={item.label}>
                          <Link to={(item as any).href}>
                            <item.icon className="h-4 w-4" />
                            <span className="flex-1">{item.label}</span>
                          </Link>
                        </SidebarMenuButton>
                      ) : (
                        <SidebarMenuButton
                          onClick={() => { onTabChange(item.id); if (isMobile) setOpenMobile(false); }}
                          isActive={activeTab === item.id}
                          tooltip={item.label}
                        >
                          <item.icon className="h-4 w-4" />
                          <span className="flex-1">{item.label}</span>
                          {item.showBadge && pendingSubmissionsCount > 0 && (
                            <Badge variant="secondary" className="ml-auto bg-warning text-warning-foreground">
                              {pendingSubmissionsCount}
                            </Badge>
                          )}
                        </SidebarMenuButton>
                      )}
                    </SidebarMenuItem>
                  ))}
                </SidebarMenu>
              </SidebarGroupContent>
            </SidebarGroup>
          );
        })}
      </SidebarContent>
    </Sidebar>
  );
}
